import { Box, CircularProgress, Grid } from "@material-ui/core";
import { InputField } from "components/InputField/InputField";
import { useFormik } from "formik";
import FinanceService from "Lib/API/Finance";
import React, { useState } from "react";
import { toastr } from "react-redux-toastr";
import Button from "../button/Button";
import addAchCardValidationSchema from "./addAchCardValidationSchema";
import { AddAchCardCardTitle } from "./Theme";
import { getAccessTokenSilently } from "utils/functions";
import { Fonts, FontWeights, FontColors } from "theme/fontConfig";

const AddAchCard = ({
  style = {},
  labelStyle = {},
  closeModal,
  refetchAchCards,
  openVerifyInfoModal,
  bottomAdd,
}) => {
  let accountID = localStorage.getItem("accountID")
    ? localStorage.getItem("accountID")
    : "";

  const [isLoading, setIsLoading] = useState(false);
  const [errorOnSubmit, setErrorOnSubmit] = useState(null);

  const formik = useFormik({
    isInitialValid: false,
    enableReinitialize: true,
    initialValues: {
      achDetails: {
        accountType: "",
        accountNumber: "",
        routingNumber: "",
        nameOnAccount: "",
      },
    },
    validationSchema: addAchCardValidationSchema,
    onSubmit: async (values) => {
      const accountDetail = {
        type: values?.achDetails?.accountType,
        number: values?.achDetails?.accountNumber,
        routing: values?.achDetails?.routingNumber,
        name: values?.achDetails?.nameOnAccount,
      };
      addAchCardHandler(accountDetail);
    },
  });

  const addAchCardHandler = async () => {
    setIsLoading(true);
    const token = await getAccessTokenSilently();

    const accountDetail = {
      type: formik?.values?.achDetails?.accountType,
      number: formik?.values?.achDetails?.accountNumber,
      routing: formik?.values?.achDetails?.routingNumber,
      name: formik?.values?.achDetails?.nameOnAccount,
    };

    FinanceService.addACHAccount(token, accountID, accountDetail)
      .then((res) => {
        toastr.success("Success", "ACH Added Successfully");
        resetForm();
        closeModal();
        openVerifyInfoModal();
        refetchAchCards();
      })
      .catch((err) => {
        setErrorOnSubmit(err?.data?.message);
        toastr.error(
          "Error",
          err?.data?.message ?? "Please enter valid card details"
        );
        setTimeout(() => {
          setErrorOnSubmit(null);
        }, 7000);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const resetForm = () => {
    formik.resetForm();
  };

  return (
    <div
      className="d-flex flex-column align-items-center"
      style={{
        gap: "20px",
        padding: 15,
      }}
    >
      <AddAchCardCardTitle>
        {bottomAdd ? "Add New ACH" : "Add New ACH Card"}
      </AddAchCardCardTitle>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <div style={{ width: "100%" }}>
            <label
              style={{
                color: FontColors.Blue002E5C,
                fontFeatureSettings: '"clig" off, "liga" off',
                fontFamily: Fonts.GeneralLabelFont.fontFamily,
                fontSize: Fonts.GeneralLabelFont.fontSize,
                fontWeight: FontWeights.Bold,
                lineHeight: "150%",
              }}
            >
              Account Type
            </label>

            <div style={{ width: "100%", position: "relative" }}>
              <select
                name="achDetails.accountType"
                value={formik.values?.achDetails?.accountType || ""}
                onChange={(e) =>
                  formik.setFieldValue("achDetails.accountType", e.target.value)
                }
                onBlur={() =>
                  formik.setFieldTouched("achDetails.accountType", true)
                }
                style={{
                  width: "100%",
                  height: 48.13,
                  boxSizing: "border-box",
                  padding: "10px 40px 10px 12px",
                  borderRadius: 5,
                  border: "1px solid #e6eef9",
                  background: "#fff",
                  fontSize: Fonts.GeneralInputFont.fontSize,
                  color: FontColors.Gray343434,
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  cursor: "pointer",
                }}
              >
                <option value="">Select account type</option>
                <option value="individual">individual</option>
                <option value="company">company</option>
              </select>
              {/* custom arrow to match input field look */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  width: 12,
                  height: 12,
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 10l5 5 5-5"
                    stroke="#9fb2d4"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            {formik.touched?.achDetails?.accountType &&
              formik.errors?.achDetails?.accountType && (
                <div style={{ color: FontColors.RedF44336, fontSize: Fonts.GeneralCaptionFont.fontSize, marginTop: 6 }}>
                  {formik.errors.achDetails.accountType}
                </div>
              )}
          </div>
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            placeholder="Account Number"
            styleWrapper={{ width: "100%" }}
            style={{ ...style, width: "100%" }}
            labelStyle={labelStyle}
            mandatory
            {...{
              formik,
              label: "Account Number",
              name: "achDetails.accountNumber",
              type: "string",
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            placeholder="Routing Number"
            styleWrapper={{ width: "100%" }}
            style={{ ...style, width: "100%" }}
            labelStyle={labelStyle}
            mandatory
            {...{
              formik,
              label: "Routing Number",
              name: "achDetails.routingNumber",
              type: "string",
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            placeholder="Name on Account"
            styleWrapper={{ width: "100%" }}
            style={{ ...style, width: "100%" }}
            labelStyle={labelStyle}
            mandatory
            {...{
              formik,
              label: "Name on Account",
              name: `achDetails.nameOnAccount`,
            }}
          />
        </Grid>
      </Grid>
      <Box>
        {errorOnSubmit && (
          <p style={{ color: FontColors.Red, fontSize: Fonts.GeneralBodyFont.fontSize }}>{errorOnSubmit}</p>
        )}
      </Box>
      <div className="add-button">
        <Button
          style={{ minWidth: "150px" }}
          onClick={formik.handleSubmit}
          variant="primary"
          size="md"
          disabled={!formik.isValid || isLoading}
        >
          {isLoading ? (
            <CircularProgress color="#FFFFFF" size={20} />
          ) : bottomAdd ? (
            "Add ACH Details"
          ) : (
            "Add ACH Card"
          )}
        </Button>
      </div>
    </div>
  );
};

export default AddAchCard;
